import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ArrowDown, Mail, Linkedin, FileText, ArrowUpRight, Github } from "lucide-react";
import { api, resolveUrl } from "../lib/api";
import { useLenis } from "../hooks/useLenis";
import { useTheme } from "../hooks/useTheme";
import { Navbar } from "../components/Navbar";
import { Marquee } from "../components/Marquee";
import { ProjectChapter } from "../components/ProjectChapter";
import { PdfViewer } from "../components/PdfViewer";

const CHAPTER_IMAGES = {
  "Data Analytics & Statistics":
    "https://images.unsplash.com/photo-1644088379091-d574269d422f?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
  "AI & Cloud Computing":
    "https://images.unsplash.com/photo-1650943574955-ac02c65cfc71?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
  "Systems Engineering & Product Management":
    "https://images.unsplash.com/photo-1576831371356-d6e9411ae501?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
  "Business Strategy & Analytics":
    "https://images.unsplash.com/photo-1522404419647-18cb51cc5c7a?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
};

const line = {
  hidden: { y: "110%" },
  show: (i) => ({
    y: 0,
    transition: { duration: 0.9, delay: 0.15 + i * 0.12, ease: [0.76, 0, 0.24, 1] },
  }),
};

const HeroLine = ({ children, i }) => (
  <span className="block overflow-hidden">
    <motion.span custom={i} variants={line} initial="hidden" animate="show" className="block">
      {children}
    </motion.span>
  </span>
);

export default function Portfolio() {
  const { dark, toggle } = useTheme();
  useLenis(true);
  const [profile, setProfile] = useState(null);
  const [categories, setCategories] = useState([]);
  const [projects, setProjects] = useState([]);
  const [viewer, setViewer] = useState({ open: false, url: null, title: "" });

  useEffect(() => {
    api.get("/profile").then(({ data }) => setProfile(data));
    api.get("/projects").then(({ data }) => {
      setCategories(data.categories);
      setProjects(data.projects);
    });
  }, []);

  const openViewer = (p) => setViewer({ open: true, url: p.pdf_url, title: p.title });

  const heroPhoto =
    profile?.photo ||
    "https://static.prod-images.emergentagent.com/jobs/6033b893-3fd9-46ac-95cf-e6d4b6e9ba04/images/68cccc40868764f2cc46845d6ae35816d8af993485181a562c2e0fd323617e4f.jpeg";

  const marqueeItems = [
    "Product Operations",
    "Business Strategy",
    "Data Analytics",
    "AI Workflows",
    "Systems Engineering",
    "CRM Systems",
  ];

  return (
    <div className="relative min-h-screen bg-[var(--bg)] text-[var(--text)]">
      <div className="noise-overlay" />
      <Navbar dark={dark} toggleTheme={toggle} />

      {/* HERO */}
      <section className="relative flex min-h-screen flex-col justify-between overflow-hidden px-5 pb-10 pt-32 sm:px-10">
        <div className="hero-grid pointer-events-none absolute inset-0" />

        <div className="relative mx-auto grid w-full max-w-[1600px] flex-1 grid-cols-1 items-center gap-10 lg:grid-cols-12 lg:gap-14">
          {/* left: intro */}
          <div className="lg:col-span-7">
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="mb-6 flex items-center gap-4"
            >
              <span className="font-mono text-xs uppercase tracking-[0.3em] text-[var(--accent)]">
                Portfolio / Index
              </span>
              <span className="h-px flex-1 max-w-[120px] bg-[var(--border)]" />
            </motion.div>

            <h1 className="font-display font-black uppercase leading-[0.85] tracking-tighter" style={{ fontSize: "clamp(2.5rem, 6.5vw, 6rem)" }}>
              <HeroLine i={0}>Bushra</HeroLine>
              <HeroLine i={1}>
                Paracha<span className="text-[var(--accent)]">.</span>
              </HeroLine>
            </h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6, duration: 0.7 }}
              className="mt-6 font-mono text-[11px] uppercase tracking-[0.22em] text-[var(--text)] sm:text-xs"
            >
              Information Systems &amp; Applied Mathematics · Stony Brook University
            </motion.p>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.72, duration: 0.7 }}
              className="mt-4 max-w-xl text-base leading-relaxed text-[var(--text-2)] sm:text-lg"
            >
              {profile?.tagline}
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.85, duration: 0.7 }}
              className="mt-8 flex flex-wrap items-center gap-3"
            >
              <a
                href="#work"
                data-testid="hero-view-work"
                className="group flex items-center gap-2 rounded-sm bg-[var(--text)] px-6 py-3.5 font-mono text-xs uppercase tracking-widest text-[var(--bg)] transition-transform duration-300 hover:-translate-y-0.5"
              >
                View Work <ArrowDown size={14} className="transition-transform group-hover:translate-y-0.5" />
              </a>
              {profile?.resume_url && (
                <a
                  href={resolveUrl(profile.resume_url)}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="hero-resume"
                  className="flex items-center gap-2 rounded-sm border border-[var(--border)] px-6 py-3.5 font-mono text-xs uppercase tracking-widest transition-colors hover:border-[var(--accent)] hover:text-[var(--accent)]"
                >
                  <FileText size={14} /> Resume
                </a>
              )}
              {profile?.linkedin && (
                <a
                  href={profile.linkedin}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="hero-linkedin"
                  className="flex items-center gap-2 rounded-sm border border-[var(--border)] px-6 py-3.5 font-mono text-xs uppercase tracking-widest transition-colors hover:border-[var(--accent)] hover:text-[var(--accent)]"
                >
                  <Linkedin size={14} /> LinkedIn
                </a>
              )}
              {profile?.github && (
                <a
                  href={profile.github}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="hero-github"
                  className="flex items-center gap-2 rounded-sm border border-[var(--border)] px-6 py-3.5 font-mono text-xs uppercase tracking-widest transition-colors hover:border-[var(--accent)] hover:text-[var(--accent)]"
                >
                  <Github size={14} /> GitHub
                </a>
              )}
            </motion.div>
          </div>

          {/* right: portrait */}
          <motion.div
            initial={{ opacity: 0, scale: 0.96, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ delay: 0.35, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className="relative lg:col-span-5"
            data-testid="hero-photo"
          >
            <div className="absolute -right-3 -top-3 h-24 w-24 rounded-sm border-t-2 border-r-2" style={{ borderColor: "var(--accent)" }} />
            <div className="absolute -bottom-3 -left-3 h-24 w-24 rounded-sm border-b-2 border-l-2" style={{ borderColor: "#DB2777" }} />
            <div className="relative overflow-hidden rounded-sm border border-[var(--border)]">
              <img
                src={heroPhoto}
                alt="Bushra Paracha"
                className="aspect-[4/5] w-full object-cover object-center"
              />
            </div>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1 }}
          className="relative mx-auto flex w-full max-w-[1600px] items-center justify-between"
        >
          <span className="flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-[var(--text-2)]">
            <ArrowDown size={14} className="animate-bounce" /> Scroll
          </span>
        </motion.div>
      </section>

      {/* ABOUT */}
      <section id="about" className="border-t border-[var(--border)] py-20 sm:py-28">
        <div className="mx-auto grid max-w-[1600px] grid-cols-12 gap-8 px-5 sm:px-10">
          <div className="col-span-12 lg:col-span-4">
            <p className="font-mono text-xs uppercase tracking-[0.3em] text-[var(--accent)]">
              (About)
            </p>
          </div>
          <div className="col-span-12 lg:col-span-8">
            <motion.p
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="font-display text-2xl font-medium leading-snug tracking-tight sm:text-3xl lg:text-4xl"
            >
              {profile?.summary}
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="mt-10 flex flex-wrap gap-2.5"
              data-testid="about-chips"
            >
              {[
                { t: "Full-stack builder", c: "#2563EB" },
                { t: "Regressions in R", c: "#0EA5A0" },
                { t: "CRM architect", c: "#DB2777" },
                { t: "AI workflows", c: "#F59E0B" },
                { t: "Figma prototyper", c: "#7C3AED" },
                { t: "B2B growth", c: "#E4572E" },
              ].map((chip) => (
                <span
                  key={chip.t}
                  className="flex items-center gap-2 rounded-full border px-4 py-2 font-mono text-[11px] uppercase tracking-wider transition-transform duration-300 hover:-translate-y-0.5"
                  style={{ borderColor: chip.c, color: chip.c }}
                >
                  <span className="h-1.5 w-1.5 rounded-full" style={{ background: chip.c }} />
                  {chip.t}
                </span>
              ))}
            </motion.div>
          </div>
        </div>
      </section>

      {/* EXPERIENCE */}
      <section id="experience" className="border-t border-[var(--border)] py-20 sm:py-28">
        <div className="mx-auto grid max-w-[1600px] grid-cols-12 gap-8 px-5 sm:px-10">
          <div className="col-span-12 lg:col-span-4">
            <p className="font-mono text-xs uppercase tracking-[0.3em] text-[var(--accent)]">
              (Experience)
            </p>
          </div>
          <div className="col-span-12 lg:col-span-8">
            <div className="flex flex-col">
              {[
                {
                  role: "TechX Intern, Product & Technology",
                  org: "Daraz",
                  loc: "Karachi, Pakistan",
                  dates: "Sep 2026 – Present",
                  current: true,
                  desc: "Building AI automations and product solutions that power Daraz's AI transformation, from concept through development, UAT, and launch, while running cross-functional discovery across Gamification, Commercial, GenOps, and Logistics teams to identify high-impact automation opportunities.",
                },
                {
                  role: "Product Manager, Business Systems & AI Automation",
                  org: "Kassam Trading Company",
                  loc: "Karachi, Pakistan",
                  dates: "Jun 2023 – Present",
                  current: true,
                  type: "Part-time",
                  desc: "Own an internal CRM supporting 300+ buyers across 22+ countries, built with React, Supabase, and PostgreSQL, with automated outreach across Brevo, WhatsApp Business, and Make.com supporting $3M+ in annual operations.",
                },
                {
                  role: "Consumer Behavior & Market Analysis Extern",
                  org: "Beats by Dre",
                  loc: "Remote",
                  dates: "Jun 2025 – Aug 2025",
                  desc: "Analyzed Gen Z behavior, pricing sensitivity, and product preferences, turning research into product positioning and marketing recommendations for stakeholders.",
                },
                {
                  role: "Student Assistant",
                  org: "Stony Brook University Graduate School",
                  loc: "Stony Brook, NY",
                  dates: "Aug 2025 – May 2026",
                  desc: "Managed graduate operations across Slate, PeopleSoft, and OnBase while maintaining accurate records and improving administrative workflows.",
                },
                {
                  role: "Teaching Assistant",
                  org: "Stony Brook University",
                  loc: "Stony Brook, NY",
                  dates: "Jan 2025 – Aug 2025",
                  desc: "Supported Applied Mathematics and Information Systems coursework through office hours, assignment feedback, and individualized instruction.",
                },
              ].map((job, i) => (
                <motion.div
                  key={job.org + job.role}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-60px" }}
                  transition={{ duration: 0.6, delay: i * 0.05, ease: [0.22, 1, 0.36, 1] }}
                  data-testid={`experience-${i}`}
                  className="grid grid-cols-12 gap-3 border-b border-[var(--border)] py-8 first:border-t"
                >
                  <div className="col-span-12 sm:col-span-4">
                    <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-[var(--text-2)]">
                      {job.dates}
                    </p>
                    {job.current && (
                      <span className="mt-2 inline-flex items-center gap-1.5 rounded-full border border-emerald-500/40 px-2.5 py-1 font-mono text-[9px] uppercase tracking-wider text-emerald-600">
                        <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" /> Current
                      </span>
                    )}
                  </div>
                  <div className="col-span-12 sm:col-span-8">
                    <div className="flex flex-wrap items-center gap-2.5">
                      <h3 className="font-display text-xl font-bold leading-tight tracking-tight sm:text-2xl">
                        {job.role}
                      </h3>
                      {job.type && (
                        <span className="rounded-full border border-[var(--border)] px-2.5 py-1 font-mono text-[9px] uppercase tracking-wider text-[var(--text-2)]">
                          {job.type}
                        </span>
                      )}
                    </div>
                    <p className="mt-1 font-mono text-[11px] uppercase tracking-[0.15em] text-[var(--accent)]">
                      {job.org} · {job.loc}
                    </p>
                    <p className="mt-3 max-w-2xl text-base leading-relaxed text-[var(--text-2)]">
                      {job.desc}
                    </p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <Marquee items={marqueeItems} />

      {/* WORK */}
      <div id="work">
        {categories.map((cat, i) => {
          const catProjects = projects.filter((p) => p.category === cat);
          if (catProjects.length === 0) return null;
          return (
            <ProjectChapter
              key={cat}
              index={i}
              category={cat}
              image={CHAPTER_IMAGES[cat]}
              projects={catProjects}
              onView={openViewer}
            />
          );
        })}
      </div>

      {/* CONTACT / FOOTER */}
      <footer
        id="contact"
        className="border-t border-[var(--border)] bg-[var(--surface)] py-20 sm:py-28"
      >
        <div className="mx-auto max-w-[1600px] px-5 sm:px-10">
          <p className="font-mono text-xs uppercase tracking-[0.3em] text-[var(--accent)]">
            (Let's connect)
          </p>
          <h2 className="mt-6 font-display font-black uppercase leading-[0.85] tracking-tighter" style={{ fontSize: "clamp(2.5rem, 9vw, 8rem)" }}>
            Let's build
            <br />
            something<span className="text-[var(--accent)]" style={{ color: "#DB2777" }}>.</span>
          </h2>

          <div className="mt-16 grid grid-cols-12 gap-8">
            <div className="col-span-12 flex flex-col gap-3 md:col-span-8">
              <a
                data-testid="contact-email"
                href={`mailto:${profile?.email}`}
                className="group flex items-center gap-3 font-display text-2xl font-bold tracking-tight sm:text-4xl"
              >
                <Mail size={26} className="text-[var(--accent)]" />
                <span className="link-underline">{profile?.email}</span>
              </a>
              <a
                data-testid="contact-linkedin"
                href={profile?.linkedin}
                target="_blank"
                rel="noreferrer"
                className="group flex items-center gap-3 font-display text-2xl font-bold tracking-tight sm:text-4xl"
              >
                <Linkedin size={26} className="text-[var(--accent)]" />
                <span className="link-underline flex items-center gap-1">
                  LinkedIn <ArrowUpRight size={20} />
                </span>
              </a>
              {profile?.github && (
                <a
                  data-testid="contact-github"
                  href={profile.github}
                  target="_blank"
                  rel="noreferrer"
                  className="group flex items-center gap-3 font-display text-2xl font-bold tracking-tight sm:text-4xl"
                >
                  <Github size={26} className="text-[var(--accent)]" />
                  <span className="link-underline flex items-center gap-1">
                    GitHub <ArrowUpRight size={20} />
                  </span>
                </a>
              )}
            </div>

            <div className="col-span-12 flex flex-col items-start gap-4 md:col-span-4 md:items-end">
              {profile?.resume_url && (
                <a
                  data-testid="download-resume"
                  href={resolveUrl(profile.resume_url)}
                  target="_blank"
                  rel="noreferrer"
                  className="flex items-center gap-2 rounded-sm bg-[var(--text)] px-6 py-4 font-mono text-xs uppercase tracking-widest text-[var(--bg)] transition-transform duration-300 hover:-translate-y-0.5"
                >
                  <FileText size={15} /> View Resume
                </a>
              )}
            </div>
          </div>

          <div className="mt-20 flex flex-col items-start justify-between gap-4 border-t border-[var(--border)] pt-8 font-mono text-[11px] uppercase tracking-[0.2em] text-[var(--text-2)] sm:flex-row sm:items-center">
            <span>© {new Date().getFullYear()} Bushra Paracha</span>
            <a href="/login" data-testid="admin-link" className="link-underline">
              Admin
            </a>
          </div>
        </div>
      </footer>

      <PdfViewer
        open={viewer.open}
        url={viewer.url}
        title={viewer.title}
        onClose={() => setViewer({ open: false, url: null, title: "" })}
      />
    </div>
  );
}
